/**
 * Derive WebSocket URL for browser → backend connections.
 *
 * Production/staging (HTTPS): uses same origin with wss: protocol.
 * Nginx reverse proxy handles /socket.io/ → backend:3000. A baked
 * `NEXT_PUBLIC_WS_URL` is honored only when it matches the current page host, so a
 * shared image (e.g. test + demo) never dials the wrong environment's socket.
 *
 * Development (HTTP): derives backend port from NEXT_PUBLIC_API_URL
 * to support worktrees (port 3010, 3020, etc.).
 */
export function getWebSocketUrl(): string {
  // SSR: use env var for internal docker networking
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3000';
  }

  // Explicit override — but ONLY when it targets the SAME host as the page.
  // A cross-host baked value (e.g. wss://test.axonfleet.com compiled into a frontend
  // image that is ALSO served on demo.axonfleet.com) would send the chatbot socket to
  // the wrong environment ("Failed to create conversation"). When the override host does
  // not match the current page host, fall through to the same-origin derivation below,
  // which is always correct regardless of what was baked at build time.
  const configured = process.env.NEXT_PUBLIC_WS_URL;
  if (configured) {
    try {
      if (new URL(configured).hostname === window.location.hostname) {
        return configured;
      }
    } catch {
      // malformed override → ignore, derive from current origin
    }
  }

  // Production/staging: HTTPS → wss: via nginx reverse proxy (same origin, no port needed)
  if (window.location.protocol === 'https:') {
    return `wss://${window.location.host}`;
  }

  // Development: HTTP → ws: with backend port from API URL
  let backendPort = '3000';
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  if (apiUrl) {
    try {
      const parsed = new URL(apiUrl);
      if (parsed.port) {
        backendPort = parsed.port;
      }
    } catch {
      // fallback to 3000
    }
  }
  return `ws://${window.location.hostname}:${backendPort}`;
}
